import { getInstance } from '../../../api/instance';
import { Paginated } from '../types';
import {
  BatchCandidate,
  BatchDetail,
  BatchStatusFilter,
  BatchSummary,
} from './types';

/**
 * Batches admin calls for the DS `abo/mod/batches` contract.
 *
 * This is the Batches sub-feature's counterpart to `printShop/api.ts`'s
 * ticket calls: same shared `getInstance()` axios client (baseURL already
 * carries the `/v1` prefix, so paths here are relative/unprefixed), same
 * 1-based UI page → 0-based DS page translation, same blob-download shape
 * for the manifest CSV as `fetchRecipientsCsv`.
 *
 * There is no dedicated backend counts endpoint for batches (unlike
 * tickets' `TICKET_COUNTS_PATH`) — `fetchBatchCounts` is a thin wrapper
 * over `fetchBatches`, reading `meta.total` off an `{status: 'open',
 * limit: 1}` query.
 */
export const BATCHES_PATH = 'abo/mod/batches';
export const BATCH_CANDIDATES_PATH = `${BATCHES_PATH}/candidates`;

export const DEFAULT_PAGE_SIZE = 25;

const instance = getInstance();

/** Translates a 1-based UI page into the 0-based page the DS paginator expects. */
const toBackendPage = (page: number): number => Math.max(0, page - 1);

export interface BatchQueueParams {
  status?: BatchStatusFilter;
  search?: string;
  page?: number; // 1-based UI page
  limit?: number;
}

/**
 * Batches listing; backend returns a `{ data, meta }` envelope. The status
 * param is omitted for "all batches" — mirrors `fetchTickets`'s `'all'`
 * handling exactly.
 */
export const fetchBatches = async (
  params: BatchQueueParams = {},
): Promise<Paginated<BatchSummary>> => {
  const {
    status = 'all',
    search = '',
    page = 1,
    limit = DEFAULT_PAGE_SIZE,
  } = params;

  const query: Record<string, string | number> = {
    page: toBackendPage(page),
    limit,
  };
  if (status !== 'all') {
    query.status = status;
  }
  const trimmedSearch = search.trim();
  if (trimmedSearch !== '') {
    query.search = trimmedSearch;
  }

  const { data } = await instance.get<Paginated<BatchSummary>>(BATCHES_PATH, {
    params: query,
  });
  return data;
};

/**
 * Open-batch total for the sidebar nav badge. No dedicated counts endpoint
 * exists for batches, so this reads `meta.total` off a minimal `open`-status
 * page-1 query instead.
 */
export const fetchBatchCounts = async (): Promise<{ open: number }> => {
  const { meta } = await fetchBatches({ status: 'open', limit: 1 });
  return { open: meta.total };
};

export interface BatchCandidatesParams {
  search?: string;
  page?: number; // 1-based UI page
  limit?: number;
}

/** Orders eligible for batching, for the builder's candidate table. */
export const fetchBatchCandidates = async (
  params: BatchCandidatesParams = {},
): Promise<Paginated<BatchCandidate>> => {
  const { search = '', page = 1, limit = DEFAULT_PAGE_SIZE } = params;

  const query: Record<string, string | number> = {
    page: toBackendPage(page),
    limit,
  };
  const trimmedSearch = search.trim();
  if (trimmedSearch !== '') {
    query.search = trimmedSearch;
  }

  const { data } = await instance.get<Paginated<BatchCandidate>>(
    BATCH_CANDIDATES_PATH,
    { params: query },
  );
  return data;
};

/** Full batch detail, including its current member orders. */
export const fetchBatchDetail = async (
  batchId: string,
): Promise<BatchDetail> => {
  const { data } = await instance.get<BatchDetail>(
    `${BATCHES_PATH}/${encodeURIComponent(batchId)}`,
  );
  return data;
};

/**
 * Shared mutation-response shape: the resulting batch plus any submitted
 * order ids the backend skipped (e.g. already batched elsewhere). Callers
 * (`BatchBuilder.tsx`) surface `skipped` in a toast — never simplify this
 * to just `BatchDetail`.
 */
export interface BatchMutationResult {
  batch: BatchDetail;
  skipped: string[];
}

/** Creates a batch and populates it with the initial selection in one call. */
export const createBatch = async (
  orderIds: string[],
): Promise<BatchMutationResult> => {
  const { data } = await instance.post<BatchMutationResult>(BATCHES_PATH, {
    orderIds,
  });
  return data;
};

/** Adds orders to an already-created, still-Open batch. */
export const addBatchOrders = async (
  batchId: string,
  orderIds: string[],
): Promise<BatchMutationResult> => {
  const { data } = await instance.post<BatchMutationResult>(
    `${BATCHES_PATH}/${encodeURIComponent(batchId)}/orders`,
    { orderIds },
  );
  return data;
};

/** Removes one order from a batch; the backend is idempotent on non-members. */
export const removeBatchOrder = async (
  batchId: string,
  orderId: string,
): Promise<BatchDetail> => {
  const { data } = await instance.delete<BatchDetail>(
    `${BATCHES_PATH}/${encodeURIComponent(batchId)}/orders/${encodeURIComponent(orderId)}`,
  );
  return data;
};

/** Removes multiple orders from a batch in one call; the backend is
 * idempotent on non-members, reported back in `skipped` — mirrors
 * `addBatchOrders`'s result shape exactly. */
export const removeBatchOrders = async (
  batchId: string,
  orderIds: string[],
): Promise<BatchMutationResult> => {
  const { data } = await instance.post<BatchMutationResult>(
    `${BATCHES_PATH}/${encodeURIComponent(batchId)}/orders/remove`,
    { orderIds },
  );
  return data;
};

/** Ships a batch; the backend answers 409 if it isn't currently Open. */
export const shipBatch = async (
  batchId: string,
  trackingRef?: string,
): Promise<BatchDetail> => {
  const body: { trackingRef?: string } = {};
  const trimmedRef = trackingRef?.trim();
  if (trimmedRef) {
    body.trackingRef = trimmedRef;
  }

  const { data } = await instance.post<BatchDetail>(
    `${BATCHES_PATH}/${encodeURIComponent(batchId)}/ship`,
    body,
  );
  return data;
};

/**
 * Manifest CSV as a blob so the api-key auth header is respected; callers
 * turn it into an object-URL download (`printShop/helpers.ts`'s
 * `downloadBlob`, reused by `BatchBuilder.tsx`). No filename extraction
 * from headers — the caller supplies the filename, matching
 * `fetchRecipientsCsv`'s existing shape exactly.
 */
export const fetchBatchManifestCsv = async (
  batchId: string,
): Promise<Blob> => {
  const { data } = await instance.get<Blob>(
    `${BATCHES_PATH}/${encodeURIComponent(batchId)}/manifest.csv`,
    { responseType: 'blob' },
  );
  return data;
};
